import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { BurgerMenu } from '../components/BurgerMenu';
import { useTheme } from '../App';
import { getImageUrl } from '../lib/supabase';
interface ProjectPageProps {
  project: {
    title: string;
    category: string | string[];
    year: string;
    image_path: string;
    thumb_path?: string;
    media_type?: 'image' | 'video';
    description?: string;
  };
  onBack: () => void;
  onGoToAdmin?: () => void;
  onGoToProjects?: () => void;
  isLoading?: boolean;
}
export function ProjectPage({
  project,
  onBack,
  onGoToAdmin,
  onGoToProjects,
  isLoading = false
}: ProjectPageProps) {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [project]);
  const getCategoryDisplay = () => {
    if (Array.isArray(project.category)) {
      return project.category.join(', ');
    }
    return project.category;
  };
  const categories = Array.isArray(project.category) ? project.category : [project.category];
  const hasVideoCategory = categories.includes('Video');
  const shouldShowVideo = hasVideoCategory || project.media_type === 'video';
  const imageUrl = getImageUrl(project.image_path);
  const handleBackToWork = () => {
    onBack();
    setTimeout(() => {
      const workSection = document.getElementById('work');
      if (workSection) {
        workSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  if (isLoading) {
    return <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : 'bg-white'}`} data-id="element-476">
        <div className="text-center" data-id="element-477">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" data-id="element-478"></div>
          <p style={{
          color: isDark ? '#999' : '#666'
        }} data-id="element-479">
            Loading project...
          </p>
        </div>
      </div>;
  }
  return <div className={`min-h-screen flex flex-col selection:bg-white selection:text-black overflow-x-hidden transition-colors duration-500 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`} data-id="element-480">
      <BurgerMenu onGoToAdmin={onGoToAdmin} onGoToProjects={onGoToProjects} onGoHome={onBack} currentPage="home" data-id="element-481" />

      {/* Same Header as HomePage */}
      <nav className="fixed top-0 left-0 w-full z-[30] pointer-events-none backdrop-blur-sm" style={{
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'
    }} data-id="element-482">
        <div className="p-4 md:p-8 flex items-center" data-id="element-483">
          <div className="pointer-events-auto" data-id="element-484">
            <button onClick={handleBackToWork} className={`text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-485">
              Jav.
            </button>
          </div>
          <div className="flex-1" data-id="element-486" />
        </div>
      </nav>

      <main className="relative z-10 pt-24 md:pt-32 pb-24 px-4 md:px-12 flex-grow" data-id="element-487">
        <div className="max-w-[1300px] mx-auto" data-id="element-488">
          <div className="flex flex-col lg:flex-row gap-12" data-id="element-489">
            {/* Left: Media */}
            <div className="flex-1 lg:w-[70%]" data-id="element-490">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              ease: [0.33, 1, 0.68, 1]
            }} data-id="element-491">
                <div className="relative w-full bg-neutral-900 rounded-sm overflow-hidden" style={{
                border: '1px solid #333'
              }} data-id="element-492">
                  {shouldShowVideo && imageUrl ? <>
                      {isVideoLoading && <div className="absolute inset-0 flex items-center justify-center bg-black z-10" data-id="element-493">
                          <div className="text-center" data-id="element-494">
                            <motion.div className="w-16 h-16 mx-auto mb-4" animate={{
                        rotate: 360
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear'
                      }} data-id="element-495">
                              <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-496">
                                <circle cx="32" cy="32" r="28" stroke="white" strokeWidth="2" strokeDasharray="4 4" opacity="0.3" data-id="element-497" />
                                <circle cx="32" cy="32" r="28" stroke="white" strokeWidth="2" strokeDasharray="44 132" strokeLinecap="round" data-id="element-498" />
                              </svg>
                            </motion.div>
                            <motion.p className="text-white text-sm uppercase tracking-widest font-light" animate={{
                        opacity: [0.5, 1, 0.5]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }} data-id="element-499">
                              Loading Video...
                            </motion.p>
                          </div>
                        </div>}
                      <video src={imageUrl} autoPlay loop muted playsInline controls className="w-full h-auto" onLoadedData={() => setIsVideoLoading(false)} onCanPlay={() => setIsVideoLoading(false)} data-id="element-500" />
                    </> : imageUrl ? <img src={imageUrl} alt={project.title} className="w-full h-auto" data-id="element-501" /> : null}
                </div>
              </motion.div>
            </div>

            {/* Right: Info Sidebar */}
            <div className="lg:w-[30%] lg:sticky lg:top-32 lg:self-start" data-id="element-502">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.33, 1, 0.68, 1]
            }} className="space-y-8" data-id="element-503">
                <div data-id="element-504">
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{
                  color: isDark ? '#fff' : '#000'
                }} data-id="element-505">
                    {project.title}
                  </h1>
                </div>

                <div className="border-t pt-6" style={{
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
              }} data-id="element-506">
                  <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-507">
                    {Array.isArray(project.category) && project.category.length > 1 ? 'Categories' : 'Category'}
                  </p>
                  <p className={`text-lg ${isDark ? 'text-white' : 'text-black'}`} data-id="element-508">
                    {getCategoryDisplay()}
                  </p>
                </div>

                <div className="border-t pt-6" style={{
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
              }} data-id="element-509">
                  <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-510">
                    Year
                  </p>
                  <p className={`text-lg ${isDark ? 'text-white' : 'text-black'}`} data-id="element-511">
                    {project.year}
                  </p>
                </div>

                <div className="border-t pt-6" style={{
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
              }} data-id="element-512">
                  <p className="text-xs uppercase tracking-widest font-light mb-4" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-513">
                    About
                  </p>
                  <p className="text-base leading-relaxed" style={{
                  color: isDark ? 'rgb(163, 163, 163)' : '#666'
                }} data-id="element-514">
                    {project.description || `A minimal and elegant ${Array.isArray(project.category) ? project.category[0].toLowerCase() : project.category.toLowerCase()} project that showcases modern design principles and attention to detail.`}
                  </p>
                </div>

                <div className="pt-6" data-id="element-515">
                  <button onClick={handleBackToWork} className={`w-full px-8 py-4 font-bold rounded-full flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-70 ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`} data-id="element-516">
                    <ArrowLeft className="w-5 h-5" data-id="element-517" />
                    <span data-id="element-518">Back to Work</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Same Footer as HomePage */}
      <footer className="relative z-10 w-full py-8 px-4 md:px-12 border-t mt-auto" style={{
      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
    }} data-id="element-519">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs md:text-sm uppercase tracking-wide font-light" style={{
        color: isDark ? '#999' : '#666'
      }} data-id="element-520">
          <span data-id="element-521">© {new Date().getFullYear()} Javstuff.com</span>
          <span data-id="element-522">All Rights Reserved</span>
        </div>
      </footer>
    </div>;
}